import type { MSelect_Option } from '@shared/components/MSelect/MSelect.vue';

/**
 * Legacy single digital download product code
 */
export const SINGLE_DIGITAL_DOWNLOAD = 'SingleDigDown';
/**
 * Legacy whole digital download product code
 */
export const WHOLE_DIGITAL_DOWNLOAD = 'WholeDigDown';
export const SINGLE_DIGITAL_DOWNLOAD_RSCODE = 'Dj';
export const WHOLE_DIGITAL_DOWNLOAD_RSCODE = 'Dk';

export const DIGITAL_DOWNLOAD_CODES = [
  SINGLE_DIGITAL_DOWNLOAD,
  WHOLE_DIGITAL_DOWNLOAD,
  SINGLE_DIGITAL_DOWNLOAD_RSCODE,
  WHOLE_DIGITAL_DOWNLOAD_RSCODE,
];

/**
 * V3-only product codes that represent multi-image digital downloads. The single ImageNode in the
 * package item is expanded at order-XML submission into one image{N} node per subject image.
 * UI hides the crop/edit button and skips rendering the subject image inside the live preview.
 * Legacy (non-RSPO) jobs are unaffected.
 */
export const V3_MULTI_IMAGE_DIGITAL_DOWNLOAD_CODES = [
  'Dk',
  'iN',
  'SE.Dk',
  'SE.iN',
  'M2',
  'M4',
  'SE.M2',
  'SE.M4',
];

/**
 * V3-only product codes that allow image switching but no cropping. The image is rendered uncropped
 * (contain-fit) and the XML is emitted with Crop "0 0 0 0". Legacy (non-RSPO) jobs are unaffected.
 */
export const V3_NO_CROP_IMAGE_CODES = ['Dj', 'iM', 'SE.Dj', 'SE.iM', 'M1', 'M3', 'SE.M1', 'SE.M3'];

/**
 * V3 multi-image code "M2" that is ALSO sold as a plain whole-subject digital download in LEGACY
 * (non-RSPO) hosting / pre-order. There it behaves like "Dk" (WHOLE_DIGITAL_DOWNLOAD_RSCODE): no
 * preview, no data collection, and all subject images are emitted at order submission. In V3/RSPO
 * jobs "M2" keeps its multi-image (image-selection) behavior and is NOT a whole download.
 */
export const LEGACY_WHOLE_DOWNLOAD_MULTI_IMAGE_CODE = 'M2';

/**
 * True when, for the given job type, this product code should be treated as a legacy whole-subject
 * digital download (no preview / no data collection; all subject images at submit). Only "M2" is
 * conditional here — "Dk"/"WholeDigDown" are always whole downloads and handled separately.
 */
export function isLegacyWholeDigitalDownload(code: string | null | undefined, isV3Job: boolean): boolean {
  if (!code) return false;
  return !isV3Job && code === LEGACY_WHOLE_DOWNLOAD_MULTI_IMAGE_CODE;
}

/**
 * Case-insensitive match against the V3 multi-image digital download code list.
 */
export function isV3MultiImageDigitalDownload(code: string | null | undefined): boolean {
  if (!code) return false;
  const check = code.trim().toLowerCase();
  return V3_MULTI_IMAGE_DIGITAL_DOWNLOAD_CODES.some((c) => c.toLowerCase() === check);
}

/**
 * Case-insensitive match against the V3 image-switchable, no-crop code list.
 */
export function isV3NoCropImage(code: string | null | undefined): boolean {
  if (!code) return false;
  const check = code.trim().toLowerCase();
  return V3_NO_CROP_IMAGE_CODES.some((c) => c.toLowerCase() === check);
}

export const PRODUCT_NAME_MAP: Record<string, string> = {
  [SINGLE_DIGITAL_DOWNLOAD]: 'Single Digital Download',
  [WHOLE_DIGITAL_DOWNLOAD]: 'Download All Images For Subject',
};

export interface ProductOption extends MSelect_Option {
  label: string;
  value: string;
}

export interface ProductLookupDisplayModel {
  lustreSpray: number;
  fineLinenTexture: number;
  pebbleTexture: number;
  mount: number;
  none: number;
  metallic: number;
  eSurface: number;
  product: string;
  rsProduct: string;
  id: number;
  cost: number;
  addWeight: number;
  shipTier: string;
  dieCut: number;
  weight: string;
  isSportsEnabled: boolean;
  isSchoolsEnabled: boolean;
}

export interface ProductLookupCreateModel {
  rsProduct: string;
  product: string;
  eSurface: number;
  metallic: number;
  none: number;
  pebbleTexture: number;
  fineLinenTexture: number;
  lustreSpray: number;
  dieCut: number;
  weight: string;
  shipTier: string;
  cost: number;
  mount: number;
  addWeight: number;
  isSportsEnabled: boolean;
  isSchoolsEnabled: boolean;
}

export interface ProductLookupUpdateModel {
  rsProduct: string;
  product: string;
  eSurface: number;
  metallic: number;
  none: number;
  pebbleTexture: number;
  fineLinenTexture: number;
  lustreSpray: number;
  dieCut: number;
  weight: string;
  shipTier: string;
  cost: number;
  mount: number;
  addWeight: number;
  isSportsEnabled: boolean;
  isSchoolsEnabled: boolean;
}

export interface PackageItemDTO {
  productCode: string;
  quantity: number;
  itemGroup: string;
}
